// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable max-lines */
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { type DayOfTheWeek } from "@src/appV2/lib/dates/types";
import { formatDistanceInMiles } from "@src/appV2/lib/utils/distance";
import { useIsStuck } from "@src/appV2/lib/utils/useIsStuck";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { PickerButton } from "@src/appV2/redesign/components/PickerButton";
import {
  areCommaSeparatedSetsEqual,
  defaultDaysOfWeek,
  defaultJobTypes,
  defaultShiftTypes,
  jobTypeOptions,
  shiftTypeOptions,
} from "@src/appV2/redesign/PlacementCandidate/constants";
import { type Worker } from "@src/appV2/Worker/api/types";
import { isEmpty, noop } from "lodash";
import { useMemo, useRef } from "react";

import { useGetWorkerOpenPlacementFilters } from "../../PlacementCandidate/api/useGetWorkerOpenPlacementFilters";
import { getDayPreferenceLabel } from "../../PlacementCandidate/utils";
import { useGetTags } from "../api/useGetTags";
import { useHasSeenPlacementFilterTooltip } from "../hooks/useHasSeenPlacementFilterTooltip";
import { PLACEMENT_PATHS, PLACEMENT_WORKPLACES_LIST_PATH } from "../paths";
import { type GetPlacementsForWorkplaceQuery } from "../types/fetchPlacements.schema";
import { type Tag } from "../types/tag";
import {
  DEFAULT_DISTANCE_FILTER_IN_MILES,
  formatSelectedTagNamesById,
  hasChildcareQualification,
} from "../utils";
import { FilterButton } from "./FilterButton";
import { PlacementFilterTooltip } from "./Filters/Tooltip";
import { getLicenseFilterLabel } from "./FiltersTopBar.utils";

const FILTER_SUMMARY_SEPARATOR = "・";
const PICKER_PRIMARY_TEXT = "Filter jobs";
const DEFAULT_DAYS_OF_WEEK_FILTER = defaultDaysOfWeek.replace(/\|/g, ",");

function getShiftTypeFilterLabel(rawShiftTypes: string | undefined) {
  if (!rawShiftTypes || areCommaSeparatedSetsEqual(rawShiftTypes, defaultShiftTypes)) {
    return undefined;
  }

  const labels = rawShiftTypes
    .split(",")
    .filter(Boolean)
    .map((value) => shiftTypeOptions.find((option) => option.value === value)?.label)
    .filter(isDefined);

  return labels.length > 0 ? labels.join(", ") : undefined;
}

function getJobTypesFilterLabel(rawJobTypes: string | undefined) {
  if (!rawJobTypes || areCommaSeparatedSetsEqual(rawJobTypes, defaultJobTypes)) {
    return undefined;
  }

  const jobTypeLabels = rawJobTypes
    .split(",")
    .map((value) => jobTypeOptions.find((option) => option.value === value)?.label)
    .filter(isDefined);

  return jobTypeLabels.length > 0 ? jobTypeLabels.join(", ") : undefined;
}

function getDaysOfWeekFilterLabel(rawDaysOfWeek: string | undefined) {
  if (!rawDaysOfWeek || areCommaSeparatedSetsEqual(rawDaysOfWeek, DEFAULT_DAYS_OF_WEEK_FILTER)) {
    return undefined;
  }

  return getDayPreferenceLabel(rawDaysOfWeek.split(",") as DayOfTheWeek[]);
}

function getDistanceFilterLabel(rawDistanceLt: number | undefined) {
  if (!isDefined(rawDistanceLt) || rawDistanceLt === DEFAULT_DISTANCE_FILTER_IN_MILES) {
    return undefined;
  }

  return formatDistanceInMiles(rawDistanceLt);
}

function getAgeGroupsFilterLabel(
  rawAgeGroups: string | undefined,
  ageGroupTags: Tag[]
): string | undefined {
  if (isEmpty(ageGroupTags) || !rawAgeGroups || isEmpty(rawAgeGroups)) {
    return undefined;
  }

  const selectedAgeGroupIds = rawAgeGroups.split(",").filter(Boolean);
  const selectedAgeGroupLabels = formatSelectedTagNamesById(selectedAgeGroupIds, ageGroupTags);

  return isEmpty(selectedAgeGroupLabels) ? undefined : selectedAgeGroupLabels;
}

function getStateFilterLabel(
  rawState: string | undefined,
  availableStates: Array<{ code: string; name: string }>,
  shouldShowStateFilter: boolean
): string | undefined {
  if (!shouldShowStateFilter || availableStates.length <= 1) {
    return undefined;
  }

  const defaultStates = availableStates.map((s) => s.code).join(",");

  if (!rawState || areCommaSeparatedSetsEqual(rawState, defaultStates)) {
    return undefined;
  }

  const stateCodes = rawState.split(",").filter(isDefined);

  if (stateCodes.length > 1) {
    const firstState = availableStates.find((state) => state.code === stateCodes[0]);
    return `${firstState?.name ?? stateCodes[0]} + ${stateCodes.length - 1}`;
  }

  const [stateCode] = stateCodes;
  const state = availableStates.find((state) => state.code === stateCode);

  return state?.name ?? stateCode;
}

interface GetPickerSecondaryTextParams {
  filter: GetPlacementsForWorkplaceQuery["filter"] | undefined;
  actualWorkerTypes: string[] | undefined;
  showWorkerTypes: boolean;
  ageGroupTags: Tag[];
  availableStates: Array<{ code: string; name: string }>;
  shouldShowStateFilter: boolean;
}

function joinFilterLabels(filterLabels: Array<string | undefined>) {
  const selectedLabels = filterLabels.filter(isDefined);

  if (selectedLabels.length === 0) {
    return undefined;
  }

  return selectedLabels.join(FILTER_SUMMARY_SEPARATOR);
}

function getPickerSecondaryText(params: GetPickerSecondaryTextParams) {
  const {
    filter,
    actualWorkerTypes,
    showWorkerTypes,
    ageGroupTags,
    availableStates,
    shouldShowStateFilter,
  } = params;

  const jobTypesLabel = getJobTypesFilterLabel(filter?.jobTypes);
  const shiftTypesLabel = getShiftTypeFilterLabel(filter?.shiftTypes);
  const daysOfWeekLabel = getDaysOfWeekFilterLabel(filter?.daysOfWeek);
  const stateLabel = getStateFilterLabel(filter?.state, availableStates, shouldShowStateFilter);
  const workerTypesLabel = isDefined(actualWorkerTypes)
    ? getLicenseFilterLabel({
        rawLicenseValues: filter?.workerTypes,
        availableLicenseValues: actualWorkerTypes,
        shouldShowLicenses: showWorkerTypes,
      })
    : undefined;
  const distanceLabel = getDistanceFilterLabel(filter?.distanceInMiles?.$lt);
  const ageGroupsLabel = getAgeGroupsFilterLabel(filter?.tags, ageGroupTags);

  return joinFilterLabels([
    jobTypesLabel,
    shiftTypesLabel,
    daysOfWeekLabel,
    stateLabel,
    workerTypesLabel,
    distanceLabel,
    ageGroupsLabel,
  ]);
}

interface FiltersTopBarProps {
  filter: GetPlacementsForWorkplaceQuery["filter"];
  setFilter?: (filter: GetPlacementsForWorkplaceQuery["filter"]) => void;
  worker: Worker;
  placementCandidateId: string;
  showBackground?: boolean;
  /**
   * List of available states with codes and names from the backend
   */
  availableStates?: Array<{ code: string; name: string }>;
  /**
   * Whether the state filter should be shown. Only true when placements exist in more than one state.
   */
  shouldShowStateFilter?: boolean;
}

export function FiltersTopBar(props: FiltersTopBarProps) {
  const {
    filter,
    setFilter = noop,
    worker,
    placementCandidateId,
    showBackground = true,
    availableStates = [] as Array<{ code: string; name: string }>,
    shouldShowStateFilter = false,
  } = props;

  const { hasSeenPlacementFilterTooltip, setHasSeenPlacementFilterTooltip } =
    useHasSeenPlacementFilterTooltip();

  const headerRef = useRef<HTMLDivElement>(null);
  const isStuck = useIsStuck(headerRef);

  const { data: workerTypes } = useGetWorkerOpenPlacementFilters({
    workerId: worker.userId,
    useHiringWorkerTypes: true,
  });

  const showWorkerTypes = isDefined(workerTypes) && workerTypes.length > 1;

  // Check if worker has childcare qualifications to enable age group tags
  const workerQualifications = worker.licensesData?.map((license) => license.qualification) ?? [];
  const isChildcareWorker = hasChildcareQualification(workerQualifications);

  const { data: tagsData } = useGetTags(
    {
      entityType: "placement",
    },
    {
      enabled: isChildcareWorker,
    }
  );

  // Filter tags to only include age group tags
  const ageGroupTags = useMemo(() => {
    if (!tagsData?.data) {
      return [];
    }

    return tagsData.data.filter((tag) => tag.attributes.metadata?.filterGroup === "ageGroup");
  }, [tagsData]);

  const pickerSecondaryText = useMemo(
    () =>
      getPickerSecondaryText({
        filter,
        actualWorkerTypes: workerTypes,
        showWorkerTypes,
        ageGroupTags,
        availableStates,
        shouldShowStateFilter,
      }),
    [filter, workerTypes, showWorkerTypes, ageGroupTags, availableStates, shouldShowStateFilter]
  );

  return (
    <Box
      ref={headerRef}
      sx={(theme) => ({
        position: "sticky",
        top: "-1px",
        zIndex: 1,
        "::before": {
          content: '""',
          display: "block",
          backgroundColor: showBackground ? theme.palette.background.tertiary : "transparent",
          position: "absolute",
          top: 0,
          zIndex: -1,
          boxSizing: "content-box",
          height: "100%",
          paddingBottom: theme.spacing(isStuck ? 5 : 6),
          width: `calc(100% + ${theme.spacing(5)} + ${theme.spacing(5)})`,
          left: `-${theme.spacing(5)}`,
          right: `-${theme.spacing(5)}`,
          borderBottom:
            showBackground && isStuck ? `1px solid ${theme.palette.border?.primary}` : "none",
          transition: showBackground ? "border-bottom 0.3s ease-in, color 0.3s ease-in" : "none",
        },
      })}
    >
      <FilterButton
        filter={filter}
        setFilter={setFilter}
        worker={worker}
        placementCandidateId={placementCandidateId}
        availableStates={availableStates}
        shouldShowStateFilter={shouldShowStateFilter}
        renderTrigger={({ open, totalAppliedFilters }) => (
          <Stack direction="row" alignItems="center" sx={{ paddingTop: isStuck ? 5 : 1 }}>
            <PlacementFilterTooltip
              hasSeenPlacementFilterTooltip={hasSeenPlacementFilterTooltip}
              setHasSeenPlacementFilterTooltip={setHasSeenPlacementFilterTooltip}
              sx={{ display: "flex", flex: 1, minWidth: 0 }}
            >
              <PickerButton
                elevated
                centered
                fullWidth
                size="medium"
                aria-label="Filter jobs"
                primaryText={PICKER_PRIMARY_TEXT}
                secondaryText={pickerSecondaryText}
                startIconType="slider-settings"
                isSelected={totalAppliedFilters > 0}
                sx={(theme) => ({
                  borderRadius: theme.borderRadius?.large,
                  backgroundColor: theme.palette.background.tertiary,
                  border: `1px solid ${theme.palette.border?.primary}`,
                  marginRight: 3,
                  minWidth: 0,
                  "& > .MuiTypography-root": {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  },
                  "& > .MuiTypography-root:first-of-type": {
                    flexShrink: 0,
                  },
                  "& > .MuiTypography-root:last-child": {
                    minWidth: 0,
                  },
                  "&:hover": {
                    backgroundColor: theme.palette.background.tertiary,
                    border: `1px solid ${theme.palette.border?.primary}`,
                  },
                  "&:active": {
                    backgroundColor: theme.palette.background.tertiary,
                    border: `1px solid ${theme.palette.border?.primary}`,
                  },
                })}
                onClick={(event) => {
                  event.currentTarget.blur();
                  setHasSeenPlacementFilterTooltip();
                  open();
                }}
              />
            </PlacementFilterTooltip>
            <IconButton
              variant="outlinedHeavy"
              aria-label="Workplaces Search"
              size="small"
              iconType="building"
              sx={{ boxShadow: "none !important" }}
              href={`${PLACEMENT_PATHS.ROOT}/${PLACEMENT_WORKPLACES_LIST_PATH}`}
              onClick={() => {
                logEvent(APP_V2_USER_EVENTS.BROWSE_PLACEMENT_WORKPLACES_SEARCH_OPENED);
              }}
            />
          </Stack>
        )}
      />
    </Box>
  );
}
